import { Stack, Typography } from '@mui/material';
import clsx from 'clsx';
import { FC } from 'react';
import './PeakDipsValuesCard.scss';
import { circadianPhaseInfo, PeakDipPhase, PeakDipStartEndSet } from '../../../../data-access';
import dayjs from 'dayjs';
import { useGeneralSettings } from '../../../../hooks/useGeneralSettings';
import { useTranslation } from 'react-i18next';
import { getCircadianPhaseTitle } from '../../i18n';

interface PeakDipsValuesCardProps {
  className?: string;
  targetPeakDipStartEndSet: PeakDipStartEndSet;
  basePeakDipStartEndSet: PeakDipStartEndSet;
  isWeekly: boolean;
  isSimplified?: boolean;
  showTitle?: boolean;
}

const PeakDipsValuesCard: FC<PeakDipsValuesCardProps> = ({
  className,
  targetPeakDipStartEndSet,
  basePeakDipStartEndSet,
  isWeekly,
  isSimplified,
  showTitle = true,
}) => {
  const { t } = useTranslation();
  const {
    generalSettings: { timeFormat },
  } = useGeneralSettings();
  const selectedDatePeaksDipsBoundaries = isWeekly
    ? basePeakDipStartEndSet
    : targetPeakDipStartEndSet;
  if (isSimplified) {
    return (
      <Stack gap={1} mt={5}>
        {Object.entries(selectedDatePeaksDipsBoundaries).map(([phase, { start, end }]) => {
          const { title, color } = circadianPhaseInfo[phase as PeakDipPhase];
          const phaseTitle = getCircadianPhaseTitle(t, phase, title);
          const startTime = dayjs(start).format(timeFormat === '24h' ? 'HH:mm' : 'h:mm A');
          const endTime = dayjs(end).format(timeFormat === '24h' ? 'HH:mm' : 'h:mm A');
          return (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              key={`${phase}-${start}-${end}`}
            >
              <Typography variant="body2">{phaseTitle}</Typography>
              <Typography style={{ color }} variant="body2">
                {`${startTime} - ${endTime}`}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    );
  }

  return (
    <div className={clsx('peak-dips-values-card', className)}>
      {showTitle && (
          <Typography variant="body1" className="peak-dips-values-card__title" mb={1}>
          {t('data.circadian.peaksAndDips')}
        </Typography>
      )}

      <div className="peak-dips-values-card__container">
        {Object.entries(targetPeakDipStartEndSet).map(([phase, { start, end }]) => {
          const { title, color } = circadianPhaseInfo[phase as PeakDipPhase];
          const phaseTitle = getCircadianPhaseTitle(t, phase, title);

          if (!start || !end) {
            return;
          }

          const startTime = dayjs(start).format(timeFormat === '24h' ? 'HH:mm' : 'h:mm A');
          const endTime = dayjs(end).format(timeFormat === '24h' ? 'HH:mm' : 'h:mm A');
          return (
            <div
              className="peak-dips-values-card__container__item"
              style={{ backgroundColor: `${color}1A` }}
              key={`${phase}-${start}-${end}`}
            >
              <div>
                <Typography
                  style={{ color }}
                  className="peak-dips-values-card__container__item__title"
                  variant={'subtitle2'}
                >
                  {phaseTitle}
                </Typography>
                <Typography
                  style={{ color }}
                  className="peak-dips-values-card__container__item__subtitle"
                  variant={'caption'}
                >{`${startTime} - ${endTime}`}</Typography>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PeakDipsValuesCard;
